/**
 * Phase 10.1 — Automated WhatsApp Intake control panel.
 *
 * Pure operational surface — never generates pricing/valuation/offer text.
 * All sends remain gated by the kill switch + canary + 24-hour window on the
 * backend. This UI just shows state and offers admin controls.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Robot, Play, Pause, ArrowClockwise, Stop, CheckCircle, XCircle,
  ShieldWarning, Eye, PencilSimple, ArrowsClockwise,
} from "@phosphor-icons/react";

const STATUS_TONE = {
  idle:      { label: "Not started", cls: "border-white/15 text-white/55 bg-white/5" },
  active:    { label: "Active",      cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  paused:    { label: "Paused",      cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  completed: { label: "Completed",   cls: "border-sky-500/40 text-sky-300 bg-sky-500/10" },
  stopped:   { label: "Stopped",     cls: "border-white/20 text-white/55 bg-white/5" },
  review:    { label: "Needs review", cls: "border-red-500/40 text-red-300 bg-red-500/10" },
};

const CONF_TONE = {
  high:   "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  medium: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  low:    "border-white/15 text-white/45 bg-white/5",
};

function tone(state) {
  if (!state) return STATUS_TONE.idle;
  if (state.needs_admin_review)  return STATUS_TONE.review;
  if (state.completed_at && !state.active) return STATUS_TONE.completed;
  if (state.paused) return STATUS_TONE.paused;
  if (state.active) return STATUS_TONE.active;
  if (!state.enabled && !state.started_at) return STATUS_TONE.idle;
  return STATUS_TONE.stopped;
}

function fmtDt(iso) {
  if (!iso) return "—";
  return iso.slice(0, 19).replace("T", " ");
}

export default function AutoIntakePanel({ lead, onRefresh }) {
  const [resp, setResp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [correctField, setCorrectField] = useState("");
  const [correctValue, setCorrectValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/leads/${lead.id}/auto-intake/state`);
      setResp(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [lead?.id]);

  const callAction = async (action, body) => {
    setBusy(action);
    try {
      await api.post(`/admin/crm/leads/${lead.id}/auto-intake/${action}`, body || {});
      await load();
      onRefresh && onRefresh();
      toast.success(`Auto-intake: ${action.replace("-", " ")} ok`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed: ${action}`);
    } finally {
      setBusy(null);
    }
  };

  const submitCorrection = async () => {
    if (!correctField.trim() || correctValue === "") {
      toast.error("Provide field and value");
      return;
    }
    let val = correctValue;
    // Smart-cast common numeric / boolean fields
    if (["year", "km_driven", "owners", "expected_price", "outstanding_loan_amount"].includes(correctField)) {
      const n = Number(val);
      if (!Number.isNaN(n)) val = n;
    }
    if (correctField === "hypothecation") {
      const s = String(val).trim().toLowerCase();
      if (s === "true" || s === "yes")  val = true;
      if (s === "false" || s === "no")  val = false;
    }
    await callAction("correct-field", { field: correctField.trim(), value: val, note: "admin_manual_override" });
    setCorrectField("");
    setCorrectValue("");
  };

  if (loading && !resp) {
    return (
      <section
        data-testid="auto-intake-panel-loading"
        className="border border-[#2A2A2A] bg-[#101010] p-4 text-xs text-white/45"
      >
        Loading automated intake state…
      </section>
    );
  }
  if (!resp) return null;

  const { state, stage_labels, stage_order, stage_required_fields, next_question_preview } = resp;
  const t = tone(state);
  const captured = state.captured_fields || {};
  const confidence = state.confidence || {};
  const log = (state.extraction_log || []).slice(-12).reverse();
  const stageIdx = stage_order.indexOf(state.current_stage);

  return (
    <section
      data-testid="auto-intake-panel"
      className="border border-[#2A2A2A] bg-[#101010]"
    >
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#222]">
        <div className="flex items-center gap-2">
          <Robot size={16} weight="bold" className="text-[#FF1E1E]" />
          <h2 className="text-[11px] font-display tracking-widest uppercase font-bold text-white/85">
            Automated WhatsApp Intake
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="auto-intake-status-pill"
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${t.cls}`}
          >
            {t.label}
          </span>
          <button
            data-testid="auto-intake-refresh"
            onClick={load}
            className="p-1 text-white/40 hover:text-white"
            aria-label="Refresh"
          >
            <ArrowsClockwise size={14} weight="bold" />
          </button>
        </div>
      </header>

      <div className="p-4 space-y-4">
        {/* Stage progress */}
        <div>
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45 mb-1.5">
            Stage progress
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="auto-intake-stages">
            {stage_order.map((s, i) => {
              const done = state.completed_stages?.includes(s);
              const cur = s === state.current_stage;
              return (
                <span
                  key={s}
                  data-testid={`auto-intake-stage-${s}`}
                  className={`px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${
                    done ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                         : cur  ? "border-[#FF1E1E] text-[#FF1E1E] bg-[#FF1E1E]/10"
                         : i < stageIdx ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                         : "border-white/15 text-white/45 bg-white/5"
                  }`}
                >
                  {i + 1}. {stage_labels[s]}
                </span>
              );
            })}
          </div>
        </div>

        {/* Next question preview */}
        {state.active && next_question_preview && (
          <div
            data-testid="auto-intake-next-question"
            className="border border-[#222] bg-black/40 px-3 py-2 text-xs text-white/75 whitespace-pre-line"
          >
            <div className="text-[10px] font-display tracking-widest uppercase text-white/45 mb-1">Next question</div>
            {next_question_preview}
          </div>
        )}

        {/* Missing fields */}
        {state.missing_fields?.length > 0 && (
          <div data-testid="auto-intake-missing" className="text-[11px] text-amber-300/85">
            <span className="font-display tracking-widest uppercase font-bold">Missing required:</span>{" "}
            <span className="font-mono-data">{state.missing_fields.join(", ")}</span>
          </div>
        )}

        {/* Pause reason */}
        {state.paused && state.pause_reason && (
          <div
            data-testid="auto-intake-pause-reason"
            className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300"
          >
            <ShieldWarning size={14} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-display tracking-widest uppercase font-bold mb-0.5">Paused</div>
              <div className="font-mono-data text-white/70">{state.pause_reason}</div>
            </div>
          </div>
        )}

        {/* Captured fields */}
        {Object.keys(captured).length > 0 && (
          <div data-testid="auto-intake-captured">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45 mb-1.5">
              Captured fields ({Object.keys(captured).length})
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              {Object.entries(captured).map(([k, v]) => {
                const c = confidence[k] || "low";
                return (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-2 border border-[#222] bg-black/30 px-2.5 py-1.5"
                    data-testid={`captured-${k}`}
                  >
                    <div className="min-w-0">
                      <div className="text-[10px] text-white/45 font-mono-data uppercase">{k}</div>
                      <div className="text-xs text-white/90 font-mono-data truncate">{String(v)}</div>
                    </div>
                    <span className={`px-1.5 py-0.5 text-[9px] font-mono-data tracking-widest uppercase border shrink-0 ${CONF_TONE[c]}`}>
                      {c}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Media */}
        {state.media_items?.length > 0 && (
          <div data-testid="auto-intake-media">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45 mb-1.5">
              Media received ({state.media_items.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {state.media_items.map((m) => (
                <span
                  key={m.id}
                  className={`px-2 py-0.5 text-[10px] font-mono-data tracking-widest uppercase border ${CONF_TONE[m.confidence] || CONF_TONE.low}`}
                >
                  {m.tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-wrap gap-1.5">
          {!state.active && !state.completed_at && (
            <button
              data-testid="auto-intake-start"
              disabled={busy === "start"}
              onClick={() => callAction("start")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
            >
              <Play size={12} weight="bold" /> Start Automated Intake
            </button>
          )}
          {state.active && (
            <button
              data-testid="auto-intake-pause"
              disabled={busy === "pause"}
              onClick={() => callAction("pause", { reason: "admin_paused" })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
            >
              <Pause size={12} weight="bold" /> Pause
            </button>
          )}
          {state.paused && (
            <button
              data-testid="auto-intake-resume"
              disabled={busy === "resume"}
              onClick={() => callAction("resume")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
            >
              <ArrowClockwise size={12} weight="bold" /> Resume
            </button>
          )}
          {(state.active || state.paused) && (
            <button
              data-testid="auto-intake-stop"
              disabled={busy === "stop"}
              onClick={() => callAction("stop", { reason: "admin_stopped" })}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-white/20 text-white/65 hover:text-white hover:border-white/50 disabled:opacity-50"
            >
              <Stop size={12} weight="bold" /> Stop
            </button>
          )}
          {(state.active || state.paused) && (
            <button
              data-testid="auto-intake-mark-review"
              disabled={busy === "mark-review"}
              onClick={() => callAction("mark-review")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-50"
            >
              <ShieldWarning size={12} weight="bold" /> Mark Needs Review
            </button>
          )}
          {Object.keys(captured).length > 0 && (
            <button
              data-testid="auto-intake-approve"
              disabled={busy === "approve-fields"}
              onClick={() => callAction("approve-fields")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
            >
              <CheckCircle size={12} weight="bold" /> Approve Captured Fields
            </button>
          )}
          <button
            data-testid="auto-intake-toggle-log"
            onClick={() => setShowLog((s) => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-white/20 text-white/55 hover:text-white"
          >
            <Eye size={12} weight="bold" /> {showLog ? "Hide" : "Show"} Audit Log
          </button>
        </div>

        {/* Inline correction form */}
        <div className="border border-[#222] bg-black/30 px-3 py-2.5 space-y-2" data-testid="auto-intake-correct-form">
          <div className="text-[10px] font-display tracking-widest uppercase text-white/45">
            Override / Correct a Field
          </div>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
            <input
              data-testid="correct-field-key"
              value={correctField}
              onChange={(e) => setCorrectField(e.target.value)}
              placeholder="field (e.g. brand)"
              className="bg-black border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono-data focus:border-white/40 outline-none"
            />
            <input
              data-testid="correct-field-value"
              value={correctValue}
              onChange={(e) => setCorrectValue(e.target.value)}
              placeholder="value"
              className="bg-black border border-[#2A2A2A] px-2 py-1.5 text-xs text-white font-mono-data focus:border-white/40 outline-none"
            />
            <button
              data-testid="correct-field-submit"
              onClick={submitCorrection}
              disabled={busy === "correct-field"}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display tracking-widest uppercase border border-white/20 text-white/85 hover:border-white/60 disabled:opacity-50"
            >
              <PencilSimple size={12} weight="bold" /> Save Override
            </button>
          </div>
        </div>

        {/* Audit log */}
        {showLog && (
          <div data-testid="auto-intake-audit-log" className="border border-[#222] bg-black/40 p-3 max-h-72 overflow-y-auto">
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-white/45 mb-2">
              Last {log.length} extraction events
            </div>
            {log.length === 0 ? (
              <div className="text-[11px] text-white/40 italic">No events yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {log.map((e) => (
                  <li key={e.id || `${e.ts}-${e.kind}`} className="text-[11px] text-white/70 leading-snug">
                    <div className="font-mono-data text-white/40">{fmtDt(e.ts)} · {e.kind}{e.stage ? ` · ${e.stage}` : ""}</div>
                    {e.extracted && Object.keys(e.extracted).length > 0 && (
                      <div className="font-mono-data text-emerald-300/85">
                        {Object.entries(e.extracted).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(" · ")}
                      </div>
                    )}
                    {e.field && (
                      <div className="font-mono-data text-sky-300/85">{e.field} ← {JSON.stringify(e.value)} {e.note ? `(${e.note})` : ""}</div>
                    )}
                    {e.reason && (
                      <div className="font-mono-data text-amber-300/85">reason: {e.reason}</div>
                    )}
                    {e.source_text && (
                      <div className="text-white/45 italic">“{e.source_text}”</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Footer meta */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] font-mono-data text-white/45 pt-1 border-t border-[#1A1A1A]">
          <div>Started: {fmtDt(state.started_at)}</div>
          <div>Last sent: {fmtDt(state.last_question_sent_at)}</div>
          <div>Last reply: {fmtDt(state.last_customer_reply_at)}</div>
          <div>Approved: {fmtDt(state.approved_at)}</div>
        </div>
      </div>
    </section>
  );
}
